import type { Transition } from '@atlassian/jira-business-board-workflow-issues';
import {
	isClientFetchError,
	isHttpClientErrorResponse,
	isValidationError,
} from '@atlassian/jira-fetch';
import { BOARD_FIELD_CATEGORY, NULL_GROUP_KEY } from '../../common/constants';
import type { BoardColumn, ColumnId } from '../../common/types';
import type { BoardIssueFieldUpdate } from '../../services/board-store/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidationOrNetworkError = (error: any): boolean =>
	// Detects client fetch, validation and 4xx errors
	error != null &&
	(isClientFetchError(error) || isValidationError(error) || isHttpClientErrorResponse(error));

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export const getColumnById = (columnId: ColumnId | null | void, boardColumns: BoardColumn[]) => {
	if (columnId == null) {
		return columnId;
	}
	const columnIdString = String(columnId);
	return boardColumns.find((column) => columnIdString === String(column.id));
};

export const getDefaultTransition = (
	sourceStatusId: number,
	targetStatusId: number,
	allTransitions: Transition[],
) =>
	allTransitions.filter(
		(t) =>
			(Number(targetStatusId) === t.toStatusId && Number(sourceStatusId) === t.fromStatusId) ||
			(t.isGlobal && Number(targetStatusId) === t.toStatusId && targetStatusId !== sourceStatusId),
	)[0];

export const isInvalidMove = ({
	sourceColumnId,
	targetColumnId,
	sourceIndex,
	destinationIndex,
	transition,
	hasMultipleTransitions,
	isGroupedByStatus,
	issueRankInProgress,
	isTargetColumnCollapsed,
}: {
	sourceColumnId: ColumnId;
	targetColumnId: ColumnId;
	sourceIndex: number;
	destinationIndex: number;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	transition: Transition | void;
	hasMultipleTransitions: boolean;
	isGroupedByStatus: boolean;
	issueRankInProgress: boolean;
	isTargetColumnCollapsed: boolean;
}) => {
	if (issueRankInProgress) {
		return true;
	}
	if (isTargetColumnCollapsed && hasMultipleTransitions && isGroupedByStatus) {
		return true;
	}
	const isSelfTransition =
		transition !== undefined && transition.fromStatusId === transition.toStatusId;
	const liftAndDropOnly =
		sourceIndex === destinationIndex &&
		sourceColumnId === targetColumnId &&
		(transition === undefined || !isSelfTransition);

	if (!isGroupedByStatus) {
		return liftAndDropOnly;
	}

	const transitionNotSelectedButRequired =
		sourceColumnId !== targetColumnId && transition === undefined && hasMultipleTransitions;

	return liftAndDropOnly || transitionNotSelectedButRequired;
};

export const getOptimisticUpdatesForPriority = (targetColumn: BoardColumn) => ({
	priorityId: targetColumn.groupKey,
	priorityIconUrl: targetColumn.imageUrl ?? undefined,
	priorityName: targetColumn.name,
});

export const getOptimisticUpdatesForAssignee = (targetColumn: BoardColumn) =>
	targetColumn.groupKey === NULL_GROUP_KEY
		? {
				assigneeKey: undefined,
				assigneeName: undefined,
				assignee: undefined,
			}
		: {
				assigneeKey: targetColumn.groupKey,
				assigneeName: targetColumn.name,
				assignee: targetColumn.imageUrl
					? {
							avatarUrl: targetColumn.imageUrl,
							displayName: targetColumn.name,
						}
					: undefined,
			};

export const getOptimisticUpdatesForCategory = (
	targetColumn: BoardColumn,
): BoardIssueFieldUpdate[] =>
	targetColumn.groupFieldKey == null
		? []
		: [
				{
					type: BOARD_FIELD_CATEGORY,
					name: targetColumn.groupName,
					fieldId: targetColumn.groupFieldKey,
					value:
						targetColumn.groupKey === NULL_GROUP_KEY || targetColumn.groupKey == null
							? undefined
							: targetColumn.groupKey,
					color: targetColumn.color,
				},
			];

const emptyArray: never[] = [];

export const getRankTarget = ({
	targetColumn,
	sourceColumnId,
	targetColumnId,
	sourceIndex,
	destinationIndex,
	isTargetColumnCollapsed,
}: {
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	targetColumn: BoardColumn | null | void;
	sourceColumnId: ColumnId;
	targetColumnId: ColumnId;
	sourceIndex: number;
	destinationIndex: number;
	isTargetColumnCollapsed: boolean;
}): {
	shouldRankAfter: boolean;
	relativeIssueId: number | null;
} => {
	const targetColumnIssues = targetColumn?.issues ?? emptyArray;
	let relativeIssueId: number | null = null;
	let shouldRankAfter = false;
	if (
		sourceColumnId === targetColumnId &&
		targetColumnIssues.length > 1 &&
		destinationIndex !== sourceIndex
	) {
		// Rank within same column
		shouldRankAfter = destinationIndex > sourceIndex;
		relativeIssueId = targetColumnIssues[destinationIndex]?.id;
	} else if (sourceColumnId !== targetColumnId && targetColumnIssues.length > 0) {
		// Rank within different columns
		if (isTargetColumnCollapsed) {
			// If the column is collapsed we should rank the card to the top
			shouldRankAfter = false;
			relativeIssueId = targetColumnIssues[0].id;
		} else {
			shouldRankAfter = targetColumnIssues[destinationIndex] === undefined;
			relativeIssueId = shouldRankAfter
				? targetColumnIssues[destinationIndex - 1]?.id
				: targetColumnIssues[destinationIndex].id;
		}
	}

	return { shouldRankAfter, relativeIssueId };
};
